<template>
  <div :class="['v-select', selectSize, 'v-input', inputSize]">
    <input
      type="text"
      class="v-select-control"
      :placeholder="placeholder"
      v-model="search"
      @input="onInput"
      @click="(showOptions = true), getAndFilterOptions()"
    />
    <ul v-if="toShowOptions" :class="['options-list', optionsList]">
      <li
        v-for="(option, index) in filteredOptions"
        :key="index"
        @click="selectOption(option)"
      >
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>

<script>
const sizes = Object.freeze({
  SM: "sm",
  MD: "md",
  LG: "lg",
  XL: "xl"
});

export default {
  name: "BaseSelectWithSearch",
  props: {
    placeholder: {
      type: String,
      default: ""
    },
    options: {
      type: Array,
      default: () => []
    },
    id: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      validator: value => Object.values(sizes).includes(value),
      default: sizes.MD
    },
    value: {
      type: [String, Number, Object],
      default: null
    }
  },
  data() {
    return {
      search: "",
      showOptions: false,
      filteredOptions: [],
      userEditing: false
    };
  },
  mounted() {
    this.getAndFilterOptions();
    this.openSelectOptions();
    this.initializeValue();
  },
  beforeDestroy() {
    this.closeSelectOptionsClickingInOutsideIt();
  },
  methods: {
    _deepEquals(a, b) {
      if (a === b) return true;
      if (
        typeof a === "object" &&
        a !== null &&
        typeof b === "object" &&
        b !== null
      ) {
        return JSON.stringify(a) === JSON.stringify(b);
      }
      return false;
    },
    _findSelected(val) {
      return this.options.find(o => this._deepEquals(o.value, val));
    },
    getAndFilterOptions() {
      return (this.filteredOptions = this.options.filter(option => {
        return option.label.toLowerCase().includes(this.search.toLowerCase());
      }));
    },
    onInput() {
      const hasText = this.search.length > 0;
      this.userEditing = hasText;
      this.$emit("input", null);
      this.$emit("filtered-option", null);
      this.$emit("typing", hasText);
      if (hasText) {
        this.showOptions = true;
        this.getAndFilterOptions();
      } else {
        this.showOptions = false;
      }
    },
    clear() {
      this.userEditing = false;
      this.search = "";
      this.showOptions = false;
      this.$emit("typing", false);
      this.getAndFilterOptions();
    },
    hideOptionsAfterClickOnIt() {
      setTimeout(() => (this.showOptions = false), 200);
    },
    selectOption(option) {
      this.userEditing = false;
      this.showOptions = false;
      this.search = option.label;
      this.$emit("input", option.value);
      this.$emit("filtered-option", option.value);
      this.$emit("typing", false);
      this.hideOptionsAfterClickOnIt();
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showOptions = false;
        this.userEditing = false;
        this.$emit("typing", false);
        if (this.value) {
          const selected = this._findSelected(this.value);
          this.search = selected ? selected.label : "";
        } else {
          this.search = "";
        }
      }
    },
    openSelectOptions() {
      document.addEventListener("click", this.handleClickOutside);
    },
    closeSelectOptionsClickingInOutsideIt() {
      document.removeEventListener("click", this.handleClickOutside);
    },
    initializeValue() {
      if (this.value) {
        const selected = this._findSelected(this.value);
        if (selected) {
          this.search = selected.label;
        }
      }
    }
  },
  computed: {
    selectSize() {
      return `v-select-${this.size}`;
    },
    inputSize() {
      return `v-input-${this.size}`;
    },
    toShowOptions() {
      return this.showOptions && this.filteredOptions.length > 0;
    },
    optionsList() {
      return this.filteredOptions.length < 3
        ? "options-list-without-scroll"
        : "options-list-with-scroll";
    }
  },
  watch: {
    value(newVal) {
      if (this.userEditing) return;
      if (newVal === null || newVal === "" || newVal === undefined) {
        this.search = "";
        return;
      }
      const selected = this._findSelected(newVal);
      if (selected) {
        this.search = selected.label;
      }
    }
  }
};
</script>
